import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAVIGATION_ITEMS } from "../../lib/constants";
import { cn } from "../../lib/utils";
import { navigationClick } from "../../lib/analytics-factory";

export function Navigation() {
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const handleMouseEnter = (name: string) => {
    setActiveDropdown(name);
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  // Helper function to check if an item or any of its children are active
  const isItemActive = (item: typeof NAVIGATION_ITEMS[0]): boolean => {
    // Direct match for the current path
    if (location.pathname === item.href) {
      return true;
    }
    
    // Check if any children match the current path
    if (item.children && item.children.length > 0) {
      return item.children.some(child => location.pathname === child.href);
    }
    
    return false;
  };

  return (
    <nav className="hidden md:flex items-center gap-6 lg:gap-8">
      {NAVIGATION_ITEMS.map((item) => {
        const isActive = isItemActive(item);
        const hasChildren = item.children && item.children.length > 0;
        
        // Hide Home link at lg breakpoint
        if (item.name === 'Home') {
          return (
            <div 
              key={item.name}
              className="relative hidden lg:block"
            >
              <Link
                to={item.href}
                className={cn(
                  'text-sm font-medium transition-colors hover:text-primary',
                  isActive ? 'text-primary' : 'text-foreground'
                )}
                onClick={navigationClick(`nav_${item.name.toLowerCase().replace(/\s+/g, '_')}`)}
              >
                {item.name}
              </Link>
            </div>
          );
        }
        
        return (
          <div 
            key={item.name}
            className="relative"
            onMouseEnter={() => hasChildren && handleMouseEnter(item.name)}
            onMouseLeave={handleMouseLeave}
          >
            {hasChildren ? (
              <>
                <button 
                  className={cn(
                    'flex items-center text-sm font-medium transition-colors hover:text-primary',
                    isActive ? 'text-primary' : 'text-foreground'
                  )}
                  onClick={navigationClick(`nav_${item.name.toLowerCase().replace(/\s+/g, '_')}_dropdown`)}
                >
                  <span className="flex items-center">
                    {item.name}
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      className="ml-1 h-4 w-4 inline-flex"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </span>
                </button>

                {activeDropdown === item.name && (
                  <>
                    <div className="absolute w-full h-3 left-0 bottom-0 translate-y-full z-10"></div>
                    <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-background ring-1 ring-black ring-opacity-5 z-10">
                      <div className="py-1" role="menu" aria-orientation="vertical">
                        {item.children && item.children.map((child) => (
                          <React.Fragment key={child.name}>
                            {child.separator && (
                              <div className="my-1 border-t border-border" />
                            )}
                            <Link
                              to={child.href}
                              className={cn(
                                'block px-4 py-2 text-sm hover:bg-accent',
                                location.pathname === child.href ? 'text-primary' : 'text-foreground',
                                child.highlighted && 'font-semibold'
                              )}
                              role="menuitem"
                              onClick={navigationClick(`nav_${item.name.toLowerCase().replace(/\s+/g, '_')}_${child.name.toLowerCase().replace(/\s+/g, '_')}`)}
                            >
                              {child.name}
                            </Link>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <Link
                to={item.href}
                className={cn(
                  'text-sm font-medium transition-colors hover:text-primary',
                  isActive ? 'text-primary' : 'text-foreground'
                )}
                onClick={navigationClick(`nav_${item.name.toLowerCase().replace(/\s+/g, '_')}`)}
              >
                {item.name}
              </Link>
            )}
          </div>
        );
      })}
      
      {/* Contact Us link */}
      <div className="relative">
        <Link
          to="/contact"
          className={cn(
            'text-sm font-medium transition-colors hover:text-primary',
            location.pathname === "/contact" ? 'text-primary' : 'text-foreground'
          )}
          onClick={navigationClick("nav_contact_us")}
        >
          Contact Us
        </Link>
      </div>
    </nav>
  );
}